/* import __COLOCATED_TEMPLATE__ from './code-blocks.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { post } from 'embercom/lib/ajax';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import type IntlService from 'embercom/services/intl';
import type WorkflowAction from 'embercom/models/workflow-connector/action';
import type Store from '@ember-data/store';
import type ThemeService from 'embercom/services/theme-service';

interface CodeBlocksArgs {
  workflowAction: WorkflowAction;
}

interface Signature {
  Args: CodeBlocksArgs;
}

export default class CodeBlocks extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: any;
  @service declare intercomEventService: any;
  @service declare store: Store;
  @service declare themeService: ThemeService;

  @tracked declare testResponse: any;

  constructor(owner: any, args: CodeBlocksArgs) {
    super(owner, args);
    this.initializeCodeBlock();
  }

  initializeCodeBlock() {
    // Initialize with default template if no code exists
    if (
      !this.args.workflowAction.codeExecutionBlock ||
      this.args.workflowAction.codeExecutionBlock.trim().length === 0
    ) {
      this.args.workflowAction.codeExecutionBlock = this.DEFAULT_CODE_TEMPLATE;
    }
  }

  readonly DEFAULT_CODE_TEMPLATE = `# You must use a return statement. You are writing Python 3.13.
# Allowed modules: math, decimal, re, datetime, datetime.timezone, json, random, time
# You can use print statements for debugging.

def transform(api_response):
	"""Transform the API response as desired
	Args:
		api_response: The raw response data from the API call
	Returns:
		dict: Returning a dict is ideal for downstream operations in data connectors.
	"""
	return api_response

return transform(inputs['data'])`;

  @action
  onChange(newCode: string) {
    this.args.workflowAction.codeExecutionBlock = newCode;
    this.args.workflowAction.codeBlockExecutionTestResult = null;
  }

  @task({ drop: true })
  *testCodeExecution(): TaskGenerator<void> {
    let params = {
      workflow_action: this.args.workflowAction,
      app_id: this.appService.app.id,
      code_execution_block: this.displayValue,
    };

    try {
      let testResponse = yield post(
        `/ember/workflow_connector/actions/${this.args.workflowAction.id}/test_code_execution`,
        params,
      );

      let { test_result } = testResponse;

      let normalizedTestResult = (
        this.store.normalize(
          'workflow-connector/code-block-execution-test-result',
          test_result,
        ) as any
      ).data.attributes;

      this.args.workflowAction.codeBlockExecutionTestResult = this.store.createFragment(
        'workflow-connector/code-block-execution-test-result',
        normalizedTestResult,
      );

      this.testResponse = null;
    } catch (exception) {
      this.args.workflowAction.codeBlockExecutionTestResult = null;
      this.testResponse = exception.jqXHR?.responseJSON;
    }

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'test_code_execution',
      action_id: this.args.workflowAction.id,
    });
  }

  get isTestCodeExecutionRunning() {
    return taskFor(this.testCodeExecution).isRunning;
  }

  get shouldShowCodeBlockTestOutput() {
    return (
      !this.isTestCodeExecutionRunning &&
      (this.args.workflowAction.codeBlockExecutionTestResult || this.testResponse)
    );
  }

  get responseBodyToDisplay() {
    if (this.args.workflowAction.codeBlockExecutionTestResult?.response?.status === 200) {
      return this.args.workflowAction.codeBlockExecutionTestResult?.response?.body
        ? JSON.stringify(
            this.args.workflowAction.codeBlockExecutionTestResult.response.body,
            null,
            2,
          )
        : '';
    } else {
      return this.testResponse?.error || '';
    }
  }

  get shouldShowStandardOutput() {
    return (
      !this.isTestCodeExecutionRunning &&
      this.args.workflowAction.codeBlockExecutionTestResult?.response?.standard_output
    );
  }

  get standardOutput() {
    return this.args.workflowAction.codeBlockExecutionTestResult?.response?.standard_output || '';
  }

  get displayValue() {
    return this.args.workflowAction.codeExecutionBlock || this.DEFAULT_CODE_TEMPLATE;
  }

  get codeMirrorTheme() {
    return this.themeService.darkModeEnabled ? 'base16-dark' : 'base16-light';
  }

  get borderColorClass() {
    return this.testResponse ? 'border-error-border' : 'border-gray-300';
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'WorkflowConnector::Builder::Body::Sections::CodeBlocks': typeof CodeBlocks;
    'workflow-connector/builder/body/sections/code-blocks': typeof CodeBlocks;
  }
}
